import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import lockupHorizontal from "./assets/brand/lockup-horizontal.svg";
import { PRICING_VISIBLE } from "./featureFlags";

// ── Scroll-reveal hook ────────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("revealed");
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal-block ${className}`}
      style={{ "--reveal-delay": `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const PLANS = [
  {
    name: "Starter",
    price: 299,
    desc: "Perfect for a single-counter shop just getting started.",
    color: "#32D4E0",
    highlight: false,
    features: [
      { text: "Up to 3 staff accounts", included: true },
      { text: "1 store", included: true },
      { text: "Fast checkout & barcode scan", included: true },
      { text: "Receipt printing", included: true },
      { text: "Basic sales reports", included: true },
      { text: "Stock management", included: true },
      { text: "Full analytics dashboard", included: false },
      { text: "Multi-store support", included: false },
      { text: "Priority support", included: false },
    ],
  },
  {
    name: "Growth",
    price: 599,
    desc: "For growing shops with a full team and deeper insights.",
    color: "#FFB340",
    highlight: true,
    features: [
      { text: "Up to 10 staff accounts", included: true },
      { text: "1 store", included: true },
      { text: "Fast checkout & barcode scan", included: true },
      { text: "Receipt printing", included: true },
      { text: "Full sales reports", included: true },
      { text: "Stock management", included: true },
      { text: "Full analytics dashboard", included: true },
      { text: "Multi-store support", included: false },
      { text: "Priority support", included: false },
    ],
  },
  {
    name: "Pro",
    price: 999,
    desc: "For serious operators running multiple locations.",
    color: "#BF5AF2",
    highlight: false,
    features: [
      { text: "Unlimited staff accounts", included: true },
      { text: "Multi-store support", included: true },
      { text: "Fast checkout & barcode scan", included: true },
      { text: "Receipt printing", included: true },
      { text: "Full sales reports + export", included: true },
      { text: "Stock management", included: true },
      { text: "Full analytics dashboard", included: true },
      { text: "Multi-store dashboard", included: true },
      { text: "Priority support", included: true },
    ],
  },
];

export default function Landing({
  onSignUp,
  onLogin,
  onThemeToggle,
  currentTheme = "dark",
  onPrivacyPolicy,
  onTerms,
}) {
  const [billingAnnual, setBillingAnnual] = useState(false);

  return (
    <div className="landing-page">
      {/* Ambient glows */}
      <div className="landing-glow-top" />
      <div className="landing-glow-bottom" />

      {/* Nav */}
      <nav className="landing-nav landing-nav-animated">
        <div className="landing-nav-brand">
          <img
            src={lockupHorizontal}
            alt="SuqPOS"
            style={{ height: "36px", width: "auto" }}
          />
        </div>
        <div className="landing-nav-actions">
          <button
            className="landing-theme-toggle"
            onClick={onThemeToggle}
            title="Toggle theme"
          >
            {currentTheme === "dark" ? "☀️" : "🌙"}
          </button>
          <button className="landing-nav-login" onClick={onLogin}>
            Log in
          </button>
          <button className="landing-nav-signup" onClick={onSignUp}>
            Sign up free
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <div className="hero-badge-wrap">
          <div className="landing-hero-badge hero-animate-1">
            🌍 Built for Ethiopian mini markets
          </div>
        </div>

        {/* Animated hero lockup — icon draws in, wordmark + tagline follow */}
        <div className="hero-lockup">
          <svg
            className="hero-lockup-icon"
            width="150"
            height="133"
            viewBox="210 62 180 160"
            fill="none"
            role="img"
          >
            <title>SuqPOS</title>
            <path
              className="hero-lockup-arch-outer"
              stroke="#32D4E0"
              strokeWidth="7"
              strokeLinecap="round"
              d="M 240 210 L 240 140 Q 240 80 300 80 Q 360 80 360 140 L 360 210"
            />
            <path
              className="hero-lockup-arch-inner"
              stroke="#FFB340"
              strokeWidth="7"
              strokeLinecap="round"
              d="M 258 210 L 258 148 Q 258 108 300 108 Q 342 108 342 148 L 342 210"
            />
            <g className="hero-lockup-details">
              <line
                x1="228"
                y1="210"
                x2="372"
                y2="210"
                stroke="#32D4E0"
                strokeWidth="5"
                strokeLinecap="round"
              />
              <circle cx="300" cy="80" r="7" fill="#32D4E0" />
              <circle cx="300" cy="108" r="5" fill="#FFB340" />
              <line
                x1="228"
                y1="175"
                x2="248"
                y2="175"
                stroke="#32D4E0"
                strokeWidth="3.5"
                strokeLinecap="round"
                opacity="0.4"
              />
              <line
                x1="352"
                y1="175"
                x2="372"
                y2="175"
                stroke="#32D4E0"
                strokeWidth="3.5"
                strokeLinecap="round"
                opacity="0.4"
              />
              <line
                x1="240"
                y1="165"
                x2="228"
                y2="158"
                stroke="#32D4E0"
                strokeWidth="3"
                strokeLinecap="round"
                opacity="0.5"
              />
              <line
                x1="360"
                y1="165"
                x2="372"
                y2="158"
                stroke="#32D4E0"
                strokeWidth="3"
                strokeLinecap="round"
                opacity="0.5"
              />
            </g>
          </svg>
          <div className="hero-lockup-wordmark">
            <span className="hero-lockup-suq">Suq</span>
            <span className="hero-lockup-pos">POS</span>
          </div>
          <p className="hero-lockup-tagline">SMART SALES · ANYWHERE</p>
        </div>

        <h1 className="landing-hero-title hero-animate-2">
          Run your shop
          <br />
          <span className="landing-hero-accent">from anywhere.</span>
        </h1>
        <p className="landing-hero-sub hero-animate-3">
          SuqPOS is a modern point-of-sale platform designed for Ethiopian mini
          markets. Manage sales, inventory, staff, and analytics — all in one
          place, on any device.
        </p>
        <div className="landing-hero-actions hero-animate-4">
          <button className="landing-cta-primary cta-pulse" onClick={onSignUp}>
            Start free — 3 months on us
          </button>
          <p className="landing-cta-note">
            No credit card required · Cancel anytime
          </p>
        </div>

        <div className="landing-preview hero-animate-5">
          <div className="landing-preview-bar">
            <span
              className="landing-preview-dot"
              style={{ background: "#FF453A" }}
            />
            <span
              className="landing-preview-dot"
              style={{ background: "#FFB340" }}
            />
            <span
              className="landing-preview-dot"
              style={{ background: "#32D74B" }}
            />
            <span
              style={{
                fontSize: "11px",
                color: "var(--text3)",
                marginLeft: "8px",
              }}
            >
              SuqPOS — Live
            </span>
          </div>
          <div className="landing-preview-body">
            <div className="landing-preview-left">
              <div className="landing-preview-search">
                🔍 Search or scan barcode...
              </div>
              <div className="landing-preview-grid">
                {[
                  {
                    name: "Coca-Cola 500ml",
                    price: "ETB 25",
                    color: "#32D4E0",
                  },
                  { name: "Bread Loaf", price: "ETB 45", color: "#FFB340" },
                  { name: "Mineral Water", price: "ETB 15", color: "#32D74B" },
                  { name: "Milk 1L", price: "ETB 60", color: "#BF5AF2" },
                  { name: "Biscuits Pack", price: "ETB 35", color: "#FF9F0A" },
                  { name: "Sugar 1kg", price: "ETB 80", color: "#32D4E0" },
                ].map((p, i) => (
                  <div
                    key={i}
                    className="landing-preview-card preview-card-hover"
                  >
                    <div
                      style={{
                        width: "100%",
                        height: "3px",
                        background: p.color,
                        borderRadius: "2px",
                        marginBottom: "6px",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "10px",
                        color: "var(--text)",
                        fontWeight: 500,
                        lineHeight: 1.3,
                      }}
                    >
                      {p.name}
                    </p>
                    <p
                      style={{
                        fontSize: "12px",
                        color: p.color,
                        fontWeight: 700,
                        marginTop: "4px",
                        fontFamily: "monospace",
                      }}
                    >
                      {p.price}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="landing-preview-right">
              <p
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "var(--text)",
                  marginBottom: "8px",
                }}
              >
                Receipt
              </p>
              {["Coca-Cola 500ml", "Bread Loaf", "Milk 1L"].map((item, i) => (
                <div key={i} className="landing-preview-item">
                  <span style={{ fontSize: "10px", color: "var(--text2)" }}>
                    {item}
                  </span>
                  <span
                    style={{
                      fontSize: "10px",
                      color: "var(--cyan)",
                      fontFamily: "monospace",
                    }}
                  >
                    ETB {[25, 45, 60][i]}.00
                  </span>
                </div>
              ))}
              <div
                style={{
                  borderTop: "1px solid var(--border)",
                  marginTop: "8px",
                  paddingTop: "8px",
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "var(--text)",
                    }}
                  >
                    TOTAL
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "var(--cyan)",
                      fontFamily: "monospace",
                    }}
                  >
                    ETB 130.00
                  </span>
                </div>
              </div>
              <div className="landing-preview-charge">CHARGE ETB 130.00</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <Reveal className="landing-stats-wrap">
        <div className="landing-stats">
          {[
            { value: "3 months", label: "Free trial" },
            { value: "ETB", label: "Native currency" },
            { value: "16", label: "Product categories" },
            { value: "Any device", label: "PC, tablet, phone" },
          ].map((s, i) => (
            <div key={i} className="landing-stat">
              <span className="landing-stat-value">{s.value}</span>
              <span className="landing-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </Reveal>

      {/* Features */}
      <section className="landing-features">
        <Reveal>
          <p className="landing-section-label">WHAT YOU GET</p>
          <h2 className="landing-section-title">Everything your shop needs</h2>
        </Reveal>
        <div className="landing-features-grid">
          {[
            {
              icon: "🧾",
              title: "Fast Checkout",
              desc: "Process sales in seconds with barcode scanning, cash, Telebirr, and bank transfer support.",
              color: "#32D4E0",
            },
            {
              icon: "📦",
              title: "Stock Management",
              desc: "Two-layer warehouse and shelf stock system with low stock alerts and expiry tracking.",
              color: "#FFB340",
            },
            {
              icon: "📊",
              title: "Sales Reports",
              desc: "Daily, weekly, and monthly reports with cashier performance breakdowns.",
              color: "#32D74B",
            },
            {
              icon: "📈",
              title: "Analytics Dashboard",
              desc: "Revenue, profit margins, top products, and busiest hours — all in one view.",
              color: "#BF5AF2",
            },
            {
              icon: "👥",
              title: "Staff Accounts",
              desc: "Create manager and cashier accounts with role-based access control.",
              color: "#FF9F0A",
            },
            {
              icon: "📱",
              title: "Works on Any Device",
              desc: "Use on a PC at the counter or check reports on your phone from anywhere.",
              color: "#32D4E0",
            },
          ].map((f, i) => (
            <Reveal key={i} delay={i * 80}>
              <div
                className="landing-feature-card feature-card-animated"
                style={{ "--feature-color": f.color }}
              >
                <div className="landing-feature-icon-wrap">
                  <div className="landing-feature-icon">{f.icon}</div>
                </div>
                <h3 className="landing-feature-title">{f.title}</h3>
                <p className="landing-feature-desc">{f.desc}</p>
                <div className="feature-card-line" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="landing-steps">
        <Reveal>
          <p className="landing-section-label">GET STARTED</p>
          <h2 className="landing-section-title">Up and running in minutes</h2>
        </Reveal>
        <div className="landing-steps-grid">
          {[
            {
              num: "01",
              title: "Create your account",
              desc: "Sign up with your email and set your password. Takes 30 seconds.",
            },
            {
              num: "02",
              title: "Register your store",
              desc: "Enter your store name, address, TIN, and contact details.",
            },
            {
              num: "03",
              title: "Add your products",
              desc: "Add products manually or import from a spreadsheet.",
            },
            {
              num: "04",
              title: "Start selling",
              desc: "Your POS is live. Add cashier accounts and start processing sales.",
            },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="landing-step step-animated">
                <div className="landing-step-num">{s.num}</div>
                <h3 className="landing-step-title">{s.title}</h3>
                <p className="landing-step-desc">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Pricing */}
      {PRICING_VISIBLE && (
      <section className="landing-pricing">
        <Reveal>
          <p className="landing-section-label">PRICING</p>
          <h2 className="landing-section-title">Simple, transparent pricing</h2>
          <p className="landing-pricing-sub">
            Start with a 3-month free trial. No credit card required.
          </p>
        </Reveal>

        <Reveal delay={100}>
          <div className="billing-toggle-wrap">
            <span className={`billing-label ${!billingAnnual ? "active" : ""}`}>
              Monthly
            </span>
            <button
              className={`billing-toggle ${billingAnnual ? "annual" : ""}`}
              onClick={() => setBillingAnnual(!billingAnnual)}
            >
              <div className="billing-toggle-knob" />
            </button>
            <span className={`billing-label ${billingAnnual ? "active" : ""}`}>
              Annual
              <span className="billing-save-badge">Save 20%</span>
            </span>
          </div>
        </Reveal>

        <Reveal delay={150}>
          <div className="pricing-trial-banner">
            🎉 All plans include a <strong>3-month free trial</strong> — no
            payment needed to get started
          </div>
        </Reveal>

        <div className="pricing-cards-grid">
          {PLANS.map((plan, i) => {
            const monthly = plan.price;
            const annual = Math.round(plan.price * 0.8);
            const displayed = billingAnnual ? annual : monthly;
            return (
              <Reveal key={i} delay={i * 100}>
                <div
                  className={`pricing-card-new ${plan.highlight ? "pricing-highlight" : ""}`}
                  style={{ "--plan-color": plan.color }}
                >
                  {plan.highlight && (
                    <div className="pricing-popular-badge">MOST POPULAR</div>
                  )}
                  <div className="pricing-card-header">
                    <h3
                      className="pricing-plan-name"
                      style={{ color: plan.color }}
                    >
                      {plan.name}
                    </h3>
                    <p className="pricing-plan-desc">{plan.desc}</p>
                    <div className="pricing-plan-amount">
                      <span className="pricing-currency">ETB</span>
                      <span className="pricing-amount-num">{displayed}</span>
                      <span className="pricing-period">/mo</span>
                    </div>
                    {billingAnnual && (
                      <p className="pricing-annual-note">
                        ETB {annual * 12}/yr · saves ETB{" "}
                        {(monthly - annual) * 12}
                      </p>
                    )}
                  </div>
                  <ul className="pricing-features-list">
                    {plan.features.map((f, j) => (
                      <li
                        key={j}
                        className={`pricing-feature-item ${f.included ? "included" : "excluded"}`}
                      >
                        <span className="pricing-feature-check">
                          {f.included ? "✓" : "✕"}
                        </span>
                        {f.text}
                      </li>
                    ))}
                  </ul>
                  <button
                    className={`pricing-plan-btn ${plan.highlight ? "pricing-plan-btn-highlight" : ""}`}
                    style={
                      plan.highlight
                        ? { background: plan.color, color: "#000" }
                        : { borderColor: plan.color, color: plan.color }
                    }
                    onClick={onSignUp}
                  >
                    Start free trial
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={200}>
          <p className="pricing-footer-note">
            All prices in Ethiopian Birr (ETB) · Billed via Chapa · Cancel
            anytime
          </p>
        </Reveal>
      </section>
      )}

      {/* Final CTA */}
      <Reveal>
        <section className="landing-final-cta final-cta-animated">
          <div className="final-cta-glow" />
          <h2 className="landing-final-title">Ready to modernize your shop?</h2>
          <p className="landing-final-sub">
            Join Ethiopian mini market owners already using SuqPOS.
          </p>
          <button className="landing-cta-primary cta-pulse" onClick={onSignUp}>
            Sign up free — no card needed
          </button>
        </section>
      </Reveal>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-brand">
          <img
            src={lockupHorizontal}
            alt="SuqPOS"
            style={{ height: "24px", width: "auto", verticalAlign: "middle" }}
          />
        </div>
        <p
          style={{ fontSize: "12px", color: "var(--text3)", marginTop: "8px" }}
        >
          © {new Date().getFullYear()} SuqPOS · Smart Sales · Anywhere
        </p>
        <p style={{ fontSize: "12px", marginTop: "10px" }}>
          <button
            className="landing-footer-link"
            onClick={onPrivacyPolicy}
          >
            Privacy Policy
          </button>
          <span style={{ color: "var(--text3)", margin: "0 8px" }}>|</span>
          <button className="landing-footer-link" onClick={onTerms}>
            Terms &amp; Conditions
          </button>
        </p>
      </footer>
    </div>
  );
}
